import { jsx as _jsx, jsxs as _jsxs } from "react/jsx-runtime";
import React, { useEffect, useRef } from 'react';
import styled, { css } from 'styled-components/macro';
import { useHistory, useLocation, useParams } from 'react-router-dom';
import { useIntl } from 'react-intl';
import { useDispatch, useSelector } from 'react-redux';
import { LISTING_PAGE_DRAFT_VARIANT, createSlug, parse, } from '@nomady/shared/utils/urlHelpers';
import { LISTING_STATE_PENDING_APPROVAL, LISTING_STATE_PUBLISHED, LISTING_STATE_CLOSED, } from '@nomady/shared/utils/types';
import { DEFAULT_SEAT_QUANTITY } from '@nomady/shared/config';
import { CampCategory, DefaultAmenity, DogPolicy, } from '@nomady/shared/types';
import blurredListingBackground from './assets/blurry-listing.png';
import { listingSelector, setCurrentPictureLightboxImage, setShowPictureLightbox, setShowPictureOverviewModal, setBookingStart, setBookingEnd, resetListingPage, loadData, setBookingData, } from './ListingPageSlice';
import PicturePreview from '../../styled-components/PicturePreview/PicturePreview';
import PictureOverview from '../../styled-components/PictureOverview/PictureOverview';
import FullPageSheet from '../../styled-components/FullPageSheet/FullPageSheet';
import PictureLightbox from '../../styled-components/PictureLightbox/PictureLightbox';
import ReviewsSection from './Sections/ReviewsSection';
import HostSection from './Sections/HostSection';
import NearbyListingsSection from './Sections/NearbyListingsSection';
import IntroSection from './Sections/IntroSection';
import { getSchema, scrollRefIntoView } from './utils';
import ListingDetailsSection from './Sections/ListingDetailsSection';
import { LayoutRow, LayoutRowBottomVariant, LayoutRowHorizontalVariant, } from '../../styled-components/Layout/Layout';
import ListingPoliciesSection from './Sections/ListingPoliciesSection';
import BookingDetailsSection from './Sections/BookingDetailsSection';
import HighlightsSection from './Sections/HighlightsSection';
import OwnListingBanner from './Sections/OwnListingBanner';
import ListingOverviewSection from './Sections/ListingOverviewSection';
import config from '../../config';
import BookingPanel from './Sections/BookingPanel';
import ListingDeletedContent from './ModalContents/ListingDeletedContent';
import ListingLoadingErrorContent from './ModalContents/ListingLoadingErrorContent';
import MobileView from '../../styled-components/MobileView/MobileView';
import CheckoutPanels from '../../styled-components/CheckoutPanels/CheckoutPanels';
import useGetAvailability from './Sections/BookingPanel/useGetAvailability';
import { DefaultTheme } from '../../theme';
import ContentPageContainer from '../../styled-components/PageContainer/ContentPageContainer';
import { LocationSectionClientOnly } from './Sections/LocationSectionClientOnly';
import Breadcrumbs from './Sections/Breadcrumbs';
export const CUSTOMER_COMMISSION_DISCOUNT_CODE_STORAGE_KEY = 'customerCommissionDiscountCode';
export const listingHasUpdatedPricing = (listing) => {
    var _a, _b;
    return !!(listing.attributes.publicData.seats !== undefined &&
        ((_a = listing.attributes.publicData.pricing) === null || _a === void 0 ? void 0 : _a.seat) !== undefined &&
        ((_b = listing.attributes.publicData.pricing) === null || _b === void 0 ? void 0 : _b.adult) !== undefined);
};
const Container = styled.div `
  @media ${props => props.theme.device.desktop} {
    display: grid;
    grid-template-columns: 2fr 1fr;
    position: relative;
  }
`;
const ListingContent = styled.div `
  width: 100%;
  padding: 20px 0px;
  gap: 20px;
  display: flex;
  flex-direction: column;
  @media ${props => props.theme.device.tablet} {
    padding: 20px 20px 20px 0px;
  }
`;
const SectionWrapper = styled.div ``;
const ListingAlertWrapper = styled.div `
  background-color: ${props => props.theme.color.pearl};
  display: flex;
  flex: 1;
`;
const ListingAlertBackground = styled.div `
  background-image: url(${blurredListingBackground});
  display: flex;
  flex: 1;
  background-position: top;
  background-size: cover;
  height: 100vh;
  align-items: flex-end;
  background-color: transparent;
  justify-content: center;
  align-items: flex-start;
  margin-top: 130px;
`;
const ContentWrapper = styled.div `
  @media ${props => props.theme.device.desktop} {
    ${props => props.hasBanner &&
    css `
        margin-top: 75px;
      `}
  }
`;
const AlertDescription = styled.div `
  width: 95%;
  height: min-content;
  padding: 30px;
  background: ${props => props.theme.color.snow};
  background-color: ${props => props.theme.color.cloud};
  border-radius: 5px;
  @media ${props => props.theme.device.tablet} {
    min-width: 600px;
    width: min-content;
  }
`;
const parseCustomerCommissionDiscountCode = (search) => {
    const urlParams = parse(`${search}`);
    const { customerCommissionDiscountCode } = urlParams;
    return customerCommissionDiscountCode || null;
};
const ListingPage = ({ params: rawParams }) => {
    var _a, _b, _c, _d, _e, _f, _g, _h, _j, _k, _l, _m, _o, _p, _q, _r, _s, _t, _u, _v, _w, _x, _y, _z, _0, _1, _2, _3;
    const intl = useIntl();
    const dispatch = useDispatch();
    const history = useHistory();
    const listing = useSelector(listingSelector);
    const { currentSearchQuery: { dates, guests }, } = useSelector((state) => state.listingSearch);
    const { reviews, showPictureOverviewModal, currentPictureLightboxImage, showPictureLightbox, fetchListingError, fetchProviderError, fetchTimeSlotsError, fetchListingInProgress, fetchProviderInProgress, fetchTimeSlotsInProgress, bookingStart, bookingEnd, bookingData, timeSlots, } = useSelector((state) => state.ListingPage);
    const { seatQuantity, adultQuantity, teenQuantity, childQuantity, extras } = bookingData;
    const { variant } = useParams();
    const { currentUser } = useSelector((state) => state.user);
    const isOwnListing = (currentUser === null || currentUser === void 0 ? void 0 : currentUser.id.uuid) === (listing === null || listing === void 0 ? void 0 : listing.author.id.uuid);
    const isPendingApproval = ((_a = listing === null || listing === void 0 ? void 0 : listing.attributes) === null || _a === void 0 ? void 0 : _a.state) === LISTING_STATE_PENDING_APPROVAL;
    const { publicData: listingPublicData } = (listing === null || listing === void 0 ? void 0 : listing.attributes) || {};
    const location = useLocation();
    useEffect(() => {
        const customerCommissionDiscountCode = parseCustomerCommissionDiscountCode(location.search);
        if (customerCommissionDiscountCode) {
            if (window === null || window === void 0 ? void 0 : window.sessionStorage) {
                sessionStorage.setItem(CUSTOMER_COMMISSION_DISCOUNT_CODE_STORAGE_KEY, `${customerCommissionDiscountCode}`);
                history.replace(location.pathname);
            }
        }
    }, [location]);
    const totalSeats = listingPublicData === null || listingPublicData === void 0 ? void 0 : listingPublicData.seats;
    const availableSeats = useGetAvailability(bookingStart, bookingEnd, timeSlots, totalSeats);
    useEffect(() => {
        if ((dates === null || dates === void 0 ? void 0 : dates.endDate) && (dates === null || dates === void 0 ? void 0 : dates.startDate)) {
            dispatch(setBookingStart(dates.startDate));
            dispatch(setBookingEnd(dates.endDate));
        }
        if (guests === null || guests === void 0 ? void 0 : guests.seats) {
            dispatch(setBookingData({ seatQuantity: guests.seats }));
        }
        if (guests === null || guests === void 0 ? void 0 : guests.adults) {
            dispatch(setBookingData({ adultQuantity: guests.adults }));
        }
        if (guests === null || guests === void 0 ? void 0 : guests.teens) {
            dispatch(setBookingData({ teenQuantity: guests.teens }));
        }
        if (guests === null || guests === void 0 ? void 0 : guests.children) {
            dispatch(setBookingData({ childQuantity: guests.children }));
        }
        if (guests === null || guests === void 0 ? void 0 : guests.infants) {
            dispatch(setBookingData({ infantQuantity: guests.infants }));
        }
    }, []);
    useEffect(() => {
        if ((listing === null || listing === void 0 ? void 0 : listing.attributes.publicData.dogsAllowed) && (guests === null || guests === void 0 ? void 0 : guests.dogs)) {
            dispatch(setBookingData({ dogQuantity: guests.dogs }));
        }
    }, [listing === null || listing === void 0 ? void 0 : listing.attributes.publicData.dogsAllowed]);
    const hostSectionRef = useRef(null);
    const reviewsSectionRef = useRef(null);
    const locationSectionRef = useRef(null);
    const mobileBookingRef = useRef(null);
    const openPicture = (id) => dispatch(setCurrentPictureLightboxImage(id));
    const isLoading = !!(fetchListingInProgress ||
        fetchProviderInProgress ||
        fetchTimeSlotsInProgress);
    const hasError = !!(fetchListingError ||
        fetchProviderError ||
        fetchTimeSlotsError);
    const loadingTitle = intl.formatMessage({
        id: 'ListingPage.loadingListingTitle',
    });
    if ((listing === null || listing === void 0 ? void 0 : listing.id.uuid) && rawParams.id !== listing.id.uuid) {
        dispatch(resetListingPage());
        dispatch(loadData({ id: rawParams.id, variant: undefined }));
        return (_jsx(ContentPageContainer, Object.assign({ title: loadingTitle }, { children: "loading" })));
    }
    if (isLoading && !listing) {
        return (_jsx(ContentPageContainer, Object.assign({ title: loadingTitle }, { children: "loading" })));
    }
    const { state } = (listing === null || listing === void 0 ? void 0 : listing.attributes) || {};
    const showListingCloseAlert = state === LISTING_STATE_CLOSED && !isOwnListing;
    const showLoadingAlert = hasError || !listing;
    const showAlert = showLoadingAlert || showListingCloseAlert;
    if (showAlert) {
        return (_jsx(ContentPageContainer, Object.assign({ title: loadingTitle }, { children: _jsx(ListingAlertWrapper, { children: _jsx(ListingAlertBackground, { children: _jsxs(AlertDescription, { children: [showLoadingAlert && _jsx(ListingLoadingErrorContent, {}), showListingCloseAlert && _jsx(ListingDeletedContent, {})] }) }) }) })));
    }
    const { siteTitle } = config;
    const listingTitle = (_b = listing === null || listing === void 0 ? void 0 : listing.attributes) === null || _b === void 0 ? void 0 : _b.title;
    const title = ((_d = (_c = listing === null || listing === void 0 ? void 0 : listing.attributes) === null || _c === void 0 ? void 0 : _c.publicData) === null || _d === void 0 ? void 0 : _d.category) === CampCategory.accommodation
        ? `${intl.formatMessage({
            id: 'ListingPage.bookYourAccommodation',
        })} - ${listingTitle} | ${siteTitle}`
        : `${intl.formatMessage({
            id: 'ListingPage.bookYourCamp',
        })} - ${listingTitle} | ${siteTitle}`;
    const imageAltTexts = ((_e = listingPublicData === null || listingPublicData === void 0 ? void 0 : listingPublicData.localizedPublicData) === null || _e === void 0 ? void 0 : _e.imageAltTexts) || {};
    const { highlights } = listing.attributes.publicData.localizedPublicData || {};
    const isAccommodation = ((_g = (_f = listing === null || listing === void 0 ? void 0 : listing.attributes) === null || _f === void 0 ? void 0 : _f.publicData) === null || _g === void 0 ? void 0 : _g.category) === CampCategory.accommodation;
    const targetRouteParams = {
        id: listing.id.uuid,
        slug: createSlug(((_h = listing === null || listing === void 0 ? void 0 : listing.attributes) === null || _h === void 0 ? void 0 : _h.title) || ''),
        type: variant === LISTING_PAGE_DRAFT_VARIANT ? 'draft' : 'edit',
        tab: variant === LISTING_PAGE_DRAFT_VARIANT ? 'photos' : 'description',
    };
    const schema = getSchema(listing, reviews);
    const getSocialMediaImages = (variant) => listing.images.map(image => image.attributes.variants[variant]);
    const listingSlug = createSlug(((_j = listing === null || listing === void 0 ? void 0 : listing.attributes) === null || _j === void 0 ? void 0 : _j.title) || '');
    const params = Object.assign(Object.assign({}, rawParams), { slug: listingSlug });
    const showWinter = ((_l = (_k = listing === null || listing === void 0 ? void 0 : listing.attributes) === null || _k === void 0 ? void 0 : _k.publicData) === null || _l === void 0 ? void 0 : _l.winterCapability) &&
        ((_o = (_m = listing === null || listing === void 0 ? void 0 : listing.attributes) === null || _m === void 0 ? void 0 : _m.publicData) === null || _o === void 0 ? void 0 : _o.descriptionWinterEnabled);
    const season = showWinter ? 'winter' : 'summer';
    const metaDescription = (_u = (_t = (_s = (_r = (_q = (_p = listing === null || listing === void 0 ? void 0 : listing.attributes) === null || _p === void 0 ? void 0 : _p.publicData) === null || _q === void 0 ? void 0 : _q.localizedPublicData) === null || _r === void 0 ? void 0 : _r.seasons) === null || _s === void 0 ? void 0 : _s[season]) === null || _t === void 0 ? void 0 : _t.description) === null || _u === void 0 ? void 0 : _u.slice(0, 200);
    const listingImageFallbackAltText = intl.formatMessage({
        id: 'ListingPage.defaultAltText',
    });
    const hasUpdatedPricing = listingHasUpdatedPricing(listing);
    const showExtras = !!(((_x = (_w = (_v = listing === null || listing === void 0 ? void 0 : listing.attributes) === null || _v === void 0 ? void 0 : _v.publicData) === null || _w === void 0 ? void 0 : _w.pricing) === null || _x === void 0 ? void 0 : _x.firewood) &&
        ((_0 = (_z = (_y = listing === null || listing === void 0 ? void 0 : listing.attributes) === null || _y === void 0 ? void 0 : _y.publicData) === null || _z === void 0 ? void 0 : _z.pricing) === null || _0 === void 0 ? void 0 : _0.powerSupply));
    return (_jsxs(ContentPageContainer, Object.assign({ title: title, author: listing.author.attributes.profile.displayName, description: metaDescription, facebookImages: getSocialMediaImages('landscape-crop'), twitterImages: getSocialMediaImages('landscape-crop'), schema: schema, customPathParams: params }, { children: [isOwnListing && (_jsx(OwnListingBanner, { goBack: history.goBack, listingState: isPendingApproval
                    ? LISTING_STATE_PENDING_APPROVAL
                    : LISTING_STATE_PUBLISHED, targetRouteParams: targetRouteParams })), _jsxs(ContentWrapper, Object.assign({ hasBanner: isOwnListing || isPendingApproval }, { children: [_jsx(Breadcrumbs, { isOwnListing: isOwnListing, isPendingApproval: isPendingApproval }), _jsx(PicturePreview, { images: listing.images, altTexts: imageAltTexts, fallbackAltText: listingImageFallbackAltText, onClickShowAllPictures: () => dispatch(setShowPictureOverviewModal(true)) }), _jsx(LayoutRow, Object.assign({ horizontalVariant: LayoutRowHorizontalVariant.MobileUncontained, bottomVariant: LayoutRowBottomVariant.ExtraSmallMargin }, { children: _jsxs(Container, { children: [_jsxs(ListingContent, { children: [_jsx(IntroSection, { onClickHostPreview: () => scrollRefIntoView(hostSectionRef), onClickAverageReview: () => scrollRefIntoView(reviewsSectionRef), onClickLocationReview: () => scrollRefIntoView(locationSectionRef) }), _jsx(MobileView, Object.assign({ breakpoint: DefaultTheme.device.desktop, ref: mobileBookingRef }, { children: hasUpdatedPricing && (_jsx(CheckoutPanels, { seatQuantity: seatQuantity, adultQuantity: adultQuantity, teenQuantity: teenQuantity, childQuantity: childQuantity, bookingStart: bookingStart, bookingEnd: bookingEnd, totalSeats: totalSeats || DEFAULT_SEAT_QUANTITY, availableSeats: availableSeats, showSeatSelection: !isAccommodation, extras: extras, showExtras: showExtras })) })), _jsx(ListingOverviewSection, { toiletAvailable: listing.attributes.publicData.toiletAvailable, showerAvailable: listing.attributes.publicData.showerAvailable, showerType: listing.attributes.publicData.showerType, openFireAllowed: listing.attributes.publicData.openFireAllowed, waterAvailable: listing.attributes.publicData.waterAvailable, dogsAllowed: listing.attributes.publicData.dogsAllowed, dogCamp: (_1 = listing.attributes.publicData.dogPolicies) === null || _1 === void 0 ? void 0 : _1.includes(DogPolicy.extraDogFriendly), powerSupplyAvailable: listing.attributes.publicData.powerSupplyAvailable, shelterAvailable: (_2 = listing.attributes.publicData.amenities) === null || _2 === void 0 ? void 0 : _2.includes(DefaultAmenity.shelter), familyFriendly: listing.attributes.publicData.familyFriendly }), _jsx(BookingDetailsSection, {}), highlights && highlights.length > 0 && (_jsx(HighlightsSection, { highlights: highlights })), _jsx(ListingDetailsSection, {}), _jsx(ListingPoliciesSection, {})] }), _jsx(BookingPanel, { disabled: isOwnListing, availableSeats: availableSeats, mobileBookingRef: mobileBookingRef })] }) })), _jsx(SectionWrapper, Object.assign({ ref: locationSectionRef }, { children: _jsx(LocationSectionClientOnly, {}) })), _jsx(SectionWrapper, Object.assign({ ref: hostSectionRef }, { children: _jsx(HostSection, {}) })), reviews && ((_3 = reviews === null || reviews === void 0 ? void 0 : reviews[0]) === null || _3 === void 0 ? void 0 : _3.length) > 0 && (_jsx(SectionWrapper, Object.assign({ ref: reviewsSectionRef }, { children: _jsx(ReviewsSection, { reviews: reviews[0] }) }))), _jsx(NearbyListingsSection, {}), _jsxs(FullPageSheet, Object.assign({ onClose: () => dispatch(setShowPictureOverviewModal(false)), open: showPictureOverviewModal }, { children: [_jsx(PictureOverview, { images: listing.images, altTexts: imageAltTexts, fallbackAltText: listingImageFallbackAltText, onOpenPicture: openPicture }), _jsx(PictureLightbox, { currentImage: currentPictureLightboxImage, images: listing.images, onChangeImage: openPicture, altTexts: imageAltTexts, fallbackAltText: listingImageFallbackAltText, onClose: () => {
                                    dispatch(setShowPictureLightbox(false));
                                }, open: !!(showPictureLightbox && currentPictureLightboxImage) })] }))] }))] })));
};
export default ListingPage;
